import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Cloud, Plus, Loader2, Trash2, RefreshCw, FolderOpen,
  Link2, Unlink, Play, Square, Eye, EyeOff, Copy,
  HardDrive, ChevronRight, ArrowUpDown, Clock, Check,
  AlertCircle, XCircle, Upload, Download, Server,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDERS = [
  { id: 'google_drive', name: 'Google Drive', icon: '🔵', color: 'bg-blue-500/10 text-blue-600 border-blue-200' },
  { id: 'dropbox', name: 'Dropbox', icon: '📦', color: 'bg-indigo-500/10 text-indigo-600 border-indigo-200' },
  { id: 'onedrive', name: 'OneDrive', icon: '☁️', color: 'bg-sky-500/10 text-sky-600 border-sky-200' },
  { id: 'ftp', name: 'FTP / SFTP', icon: '🖥️', color: 'bg-amber-500/10 text-amber-600 border-amber-200' },
];

const DIRECTIONS = [
  { value: 'import', label: 'Import (Cloud → Galeria)', icon: Download },
  { value: 'export', label: 'Export (Galeria → Cloud)', icon: Upload },
  { value: 'bidirectional', label: 'Oba kierunki', icon: ArrowUpDown },
];

export default function CloudPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('connections');
  const [connections, setConnections] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [syncJobs, setSyncJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [pairDialog, setPairDialog] = useState(null);
  const [ftpDialog, setFtpDialog] = useState(false);
  const [ftpCredentials, setFtpCredentials] = useState(null);
  const [folderBrowser, setFolderBrowser] = useState(null);

  // Polling
  const pollingRef = useRef(null);

  const fetchConnections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cloud/connections`);
      setConnections(res.data.connections || []);
    } catch { /* ignore */ }
  }, []);

  const fetchGalleries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/galleries`);
      setGalleries(res.data.galleries || res.data || []);
    } catch { /* ignore */ }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cloud/sync/jobs?limit=50`);
      setSyncJobs(res.data.jobs || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchConnections(), fetchGalleries(), fetchJobs()]);
      setLoading(false);
    };
    load();
  }, [fetchConnections, fetchGalleries, fetchJobs]);

  // Poll running jobs
  useEffect(() => {
    const hasRunning = syncJobs.some(j => j.status === 'running' || j.status === 'pending');
    if (hasRunning && !pollingRef.current) {
      pollingRef.current = setInterval(() => { fetchJobs(); }, 3000);
    } else if (!hasRunning && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [syncJobs, fetchJobs]);

  // Check URL params for OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    if (connected) {
      toast.success(`Połączono z ${connected.replace('_', ' ')}`);
      fetchConnections();
      window.history.replaceState({}, '', '/cloud');
    }
    const error = params.get('error');
    if (error) {
      toast.error(`Błąd OAuth: ${error}`);
      window.history.replaceState({}, '', '/cloud');
    }
  }, [fetchConnections]);

  // ==================== CONNECT / DISCONNECT ====================

  const handleConnect = async (providerId) => {
    if (providerId === 'ftp') {
      setFtpDialog(true);
      return;
    }

    try {
      const redirectUri = `${process.env.REACT_APP_BACKEND_URL}/api/cloud/oauth/${providerId === 'google_drive' ? 'google' : providerId}/callback`;
      const res = await axios.post(`${API}/cloud/connections/${providerId}/auth-url`, {
        redirect_uri: redirectUri,
      });
      // Open OAuth popup
      const w = 600, h = 700;
      const left = (window.screen.width - w) / 2;
      const top = (window.screen.height - h) / 2;
      window.open(res.data.auth_url, 'oauth', `width=${w},height=${h},left=${left},top=${top}`);
    } catch (err) {
      toast.error('Nie udało się rozpocząć autoryzacji');
    }
  };

  const handleDisconnect = async (connectionId) => {
    if (!window.confirm('Na pewno chcesz rozłączyć to połączenie?')) return;
    try {
      await axios.delete(`${API}/cloud/connections/${connectionId}`);
      toast.success('Połączenie rozłączone');
      fetchConnections();
    } catch {
      toast.error('Błąd rozłączania');
    }
  };

  // ==================== FTP ====================

  const handleCreateFtp = async (galleryId, displayName) => {
    try {
      const res = await axios.post(`${API}/cloud/ftp/accounts`, {
        gallery_id: galleryId,
        display_name: displayName || 'FTP Upload',
      });
      setFtpCredentials(res.data);
      setFtpDialog(false);
      toast.success('Konto FTP utworzone');
      fetchConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd tworzenia konta FTP');
    }
  };

  const handleDeleteFtp = async (connectionId) => {
    if (!window.confirm('Usunąć konto FTP?')) return;
    try {
      await axios.delete(`${API}/cloud/ftp/accounts/${connectionId}`);
      toast.success('Konto FTP usunięte');
      fetchConnections();
    } catch {
      toast.error('Błąd usuwania');
    }
  };

  // ==================== SYNC PAIRS ====================

  const handleCreatePair = async (connectionId, galleryId, folderId, folderPath, direction) => {
    try {
      await axios.post(`${API}/cloud/connections/${connectionId}/sync-pairs`, {
        gallery_id: galleryId,
        cloud_folder_id: folderId,
        cloud_folder_path: folderPath,
        direction: direction,
      });
      toast.success('Para synchronizacji utworzona');
      setPairDialog(null);
      fetchConnections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd tworzenia pary');
    }
  };

  const handleDeletePair = async (connectionId, pairId) => {
    if (!window.confirm('Usunąć powiązanie?')) return;
    try {
      await axios.delete(`${API}/cloud/connections/${connectionId}/sync-pairs/${pairId}`);
      toast.success('Powiązanie usunięte');
      fetchConnections();
    } catch {
      toast.error('Błąd usuwania');
    }
  };

  // ==================== SYNC TRIGGER ====================

  const handleTriggerSync = async (connectionId, pairId, direction) => {
    try {
      await axios.post(`${API}/cloud/sync/trigger`, {
        connection_id: connectionId,
        sync_pair_id: pairId,
        direction: direction,
      });
      toast.success('Synchronizacja rozpoczęta');
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd uruchamiania synchronizacji');
    }
  };

  const handleCancelJob = async (jobId) => {
    try {
      await axios.post(`${API}/cloud/sync/jobs/${jobId}/cancel`);
      toast.success('Anulowanie żądane');
      fetchJobs();
    } catch {
      toast.error('Błąd anulowania');
    }
  };

  // ==================== FOLDER BROWSER ====================

  const handleBrowseFolders = async (connectionId, folderId = null) => {
    try {
      const params = folderId ? `?folder_id=${folderId}` : '';
      const res = await axios.get(`${API}/cloud/connections/${connectionId}/folders${params}`);
      setFolderBrowser(prev => ({
        connectionId,
        items: res.data.items || [],
        path: prev?.connectionId === connectionId ? [...(prev.path || []), { id: folderId, name: folderId || 'Root' }] : [{ id: null, name: 'Root' }],
        selectedFolder: null,
      }));
    } catch (err) {
      toast.error('Błąd ładowania folderów');
    }
  };

  // ==================== RENDER HELPERS ====================

  const getProviderInfo = (providerId) => PROVIDERS.find(p => p.id === providerId) || PROVIDERS[0];
  const getConnection = (providerId) => connections.find(c => c.provider === providerId);

  const getJobStatusBadge = (status) => {
    const map = {
      pending: { variant: 'secondary', icon: Clock, label: 'Oczekuje' },
      running: { variant: 'default', icon: Loader2, label: 'W toku', spin: true },
      completed: { variant: 'success', icon: Check, label: 'Zakończony' },
      failed: { variant: 'destructive', icon: XCircle, label: 'Błąd' },
      cancelled: { variant: 'outline', icon: AlertCircle, label: 'Anulowany' },
    };
    const info = map[status] || map.pending;
    return (
      <Badge variant={info.variant} className="gap-1">
        <info.icon className={`w-3 h-3 ${info.spin ? 'animate-spin' : ''}`} />
        {info.label}
      </Badge>
    );
  };

  // ==================== TAB: CONNECTIONS ====================

  const renderConnections = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {PROVIDERS.map(provider => {
        const conn = getConnection(provider.id);
        return (
          <Card key={provider.id} className={`border ${conn ? 'border-green-300' : 'border-border'}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{provider.icon}</span>
                  <div>
                    <CardTitle className="text-lg">{provider.name}</CardTitle>
                    {conn && (
                      <p className="text-sm text-muted-foreground">{conn.account_email || conn.ftp_username}</p>
                    )}
                  </div>
                </div>
                <Badge variant={conn ? 'default' : 'secondary'}>
                  {conn ? 'Połączony' : 'Rozłączony'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {conn ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {conn.sync_pairs?.length || 0} powiązań z galeriami
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPairDialog(conn)}>
                      <Plus className="w-4 h-4 mr-1" /> Dodaj parę
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => {
                      if (provider.id === 'ftp') handleDeleteFtp(conn.id);
                      else handleDisconnect(conn.id);
                    }}>
                      <Unlink className="w-4 h-4 mr-1" /> Rozłącz
                    </Button>
                  </div>
                </div>
              ) : (
                <Button onClick={() => handleConnect(provider.id)}>
                  <Link2 className="w-4 h-4 mr-2" />
                  {provider.id === 'ftp' ? 'Utwórz konto' : 'Połącz'}
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  // ==================== TAB: SYNC PAIRS ====================

  const renderSyncPairs = () => {
    const allPairs = [];
    connections.forEach(conn => {
      (conn.sync_pairs || []).forEach(pair => {
        allPairs.push({ ...pair, connection: conn });
      });
    });

    if (allPairs.length === 0) {
      return (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <ArrowUpDown className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Brak powiązań synchronizacji.</p>
            <p className="text-sm mt-1">Przejdź do zakładki „Połączenia" i dodaj parę galeria ↔ folder.</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {allPairs.map(pair => {
          const provInfo = getProviderInfo(pair.connection.provider);
          const dirInfo = DIRECTIONS.find(d => d.value === pair.direction) || DIRECTIONS[0];
          return (
            <Card key={pair.id}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{provInfo.icon}</span>
                    <div>
                      <p className="font-medium">{pair.gallery_name || pair.gallery_id}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <FolderOpen className="w-3 h-3" />
                        {pair.cloud_folder_path || 'Root'}
                      </p>
                    </div>
                    <Badge variant="outline" className="gap-1">
                      <dirInfo.icon className="w-3 h-3" />
                      {dirInfo.label}
                    </Badge>
                    {pair.last_synced_at && (
                      <span className="text-xs text-muted-foreground">
                        Ostatni sync: {new Date(pair.last_synced_at).toLocaleString('pl')}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {pair.direction !== 'export' && (
                      <Button size="sm" onClick={() => handleTriggerSync(pair.connection.id, pair.id, 'import')}>
                        <Download className="w-4 h-4 mr-1" /> Import
                      </Button>
                    )}
                    {pair.direction !== 'import' && (
                      <Button size="sm" variant="outline" onClick={() => handleTriggerSync(pair.connection.id, pair.id, 'export')}>
                        <Upload className="w-4 h-4 mr-1" /> Export
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeletePair(pair.connection.id, pair.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  // ==================== TAB: HISTORY ====================

  const renderHistory = () => {
    if (syncJobs.length === 0) {
      return (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Brak historii synchronizacji.</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Kierunek</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Pliki</TableHead>
              <TableHead>Rozmiar</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {syncJobs.map(job => {
              const provInfo = getProviderInfo(job.provider);
              const progress = job.live_progress;
              const processed = progress ? progress.processed : job.processed_files;
              const total = progress ? progress.total : job.total_files;
              const isRunning = job.status === 'running' || job.status === 'pending';

              return (
                <TableRow key={job.id}>
                  <TableCell className="text-sm">{new Date(job.created_at).toLocaleString('pl')}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1">
                      {provInfo.icon} {provInfo.name}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {job.direction === 'import' ? '↓ Import' : '↑ Export'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {getJobStatusBadge(job.status)}
                      {isRunning && total > 0 && (
                        <div className="w-32">
                          <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all"
                              style={{ width: `${Math.round((processed / total) * 100)}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {processed}/{total}
                          </p>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {job.processed_files || 0} ok
                      {job.skipped_files > 0 && `, ${job.skipped_files} pominięto`}
                      {job.failed_files > 0 && (
                        <span className="text-destructive">, {job.failed_files} błąd</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatBytes(job.bytes_transferred || 0)}
                  </TableCell>
                  <TableCell>
                    {isRunning && (
                      <Button size="sm" variant="ghost" onClick={() => handleCancelJob(job.id)}>
                        <Square className="w-4 h-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    );
  };

  // ==================== TAB: FTP ====================

  const renderFtp = () => {
    const ftpConns = connections.filter(c => c.provider === 'ftp');
    const ftpAccounts = [];
    ftpConns.forEach(conn => {
      (conn.sync_pairs || []).forEach(sp => {
        ftpAccounts.push({
          connectionId: conn.id,
          pairId: sp.id,
          username: conn.ftp_username,
          host: conn.ftp_host,
          port: conn.ftp_port,
          galleryName: sp.gallery_name || sp.gallery_id,
          autoSync: sp.auto_sync,
        });
      });
    });

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Konta SFTP</h3>
            <p className="text-sm text-muted-foreground">
              Konta do uploadu z aparatu lub Lightrooma bezpośrednio na serwer.
            </p>
          </div>
          <Button onClick={() => setFtpDialog(true)}>
            <Plus className="w-4 h-4 mr-2" /> Nowe konto
          </Button>
        </div>

        {ftpAccounts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Server className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Brak kont SFTP.</p>
              <p className="text-sm mt-1">Utwórz konto i konfiguruj Lightroom lub aparat do automatycznego uploadu.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {ftpAccounts.map((acc, i) => (
              <Card key={i}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="space-y-1">
                      <p className="font-medium flex items-center gap-2">
                        <Server className="w-4 h-4" /> {acc.galleryName}
                      </p>
                      <div className="flex gap-4 text-sm text-muted-foreground">
                        <span>Host: <code className="bg-muted px-1 rounded">{acc.host}</code></span>
                        <span>Port: <code className="bg-muted px-1 rounded">{acc.port}</code></span>
                        <span>User: <code className="bg-muted px-1 rounded">{acc.username}</code></span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => {
                        navigator.clipboard.writeText(`sftp -P ${acc.port} ${acc.username}@${acc.host}`);
                        toast.success('Komenda skopiowana');
                      }}>
                        <Copy className="w-4 h-4 mr-1" /> Kopiuj
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteFtp(acc.connectionId)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Lightroom instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Instrukcje konfiguracji</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <p className="font-medium mb-1">Lightroom Classic:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>File → Auto Import → Auto Import Settings</li>
                <li>Watched Folder: ustaw folder lokalny</li>
                <li>Użyj wtyczki FTP Export lub skryptu do kopiowania na SFTP</li>
              </ol>
            </div>
            <div>
              <p className="font-medium mb-1">Capture One:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Output → Process Recipe → SFTP destination</li>
                <li>Server: host i port z powyżej</li>
                <li>Username i hasło z momentu tworzenia konta</li>
              </ol>
            </div>
            <div>
              <p className="font-medium mb-1">Linia poleceń:</p>
              <code className="block bg-muted px-3 py-2 rounded text-xs">
                sftp -P 2222 username@host
              </code>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ==================== DIALOGS ====================

  const renderPairDialog = () => {
    if (!pairDialog) return null;

    return (
      <SyncPairDialog
        connection={pairDialog}
        galleries={galleries}
        onClose={() => { setPairDialog(null); setFolderBrowser(null); }}
        onBrowse={(folderId) => handleBrowseFolders(pairDialog.id, folderId)}
        folderBrowser={folderBrowser}
        onSubmit={handleCreatePair}
      />
    );
  };

  const renderFtpDialog = () => (
    <Dialog open={ftpDialog} onOpenChange={setFtpDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nowe konto SFTP</DialogTitle>
        </DialogHeader>
        <FtpCreateForm galleries={galleries} onSubmit={handleCreateFtp} onClose={() => setFtpDialog(false)} />
      </DialogContent>
    </Dialog>
  );

  const renderFtpCredentials = () => {
    if (!ftpCredentials) return null;
    return (
      <Dialog open={!!ftpCredentials} onOpenChange={() => setFtpCredentials(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Konto SFTP utworzone</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-destructive font-medium">
              Zapisz hasło — nie będzie możliwe ponowne wyświetlenie!
            </p>
            <div className="bg-muted p-4 rounded space-y-2 text-sm font-mono">
              <p>Host: {ftpCredentials.ftp_host}</p>
              <p>Port: {ftpCredentials.ftp_port}</p>
              <p>Username: {ftpCredentials.ftp_username}</p>
              <p>Password: {ftpCredentials.ftp_password}</p>
            </div>
            <Button className="w-full" onClick={() => {
              navigator.clipboard.writeText(
                `Host: ${ftpCredentials.ftp_host}\nPort: ${ftpCredentials.ftp_port}\nUsername: ${ftpCredentials.ftp_username}\nPassword: ${ftpCredentials.ftp_password}`
              );
              toast.success('Dane skopiowane do schowka');
            }}>
              <Copy className="w-4 h-4 mr-2" /> Kopiuj wszystko
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // ==================== MAIN RENDER ====================

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/cloud" />
      <main className="lg:ml-64 p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <div className="flex items-center gap-3 mb-6">
            <Cloud className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Focus Cloud</h1>
              <p className="text-sm text-muted-foreground">
                Synchronizacja galerii z chmurami i serwerem FTP
              </p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="connections">Połączenia</TabsTrigger>
                <TabsTrigger value="sync">Synchronizacja</TabsTrigger>
                <TabsTrigger value="history">Historia</TabsTrigger>
                <TabsTrigger value="ftp">FTP</TabsTrigger>
              </TabsList>

              <TabsContent value="connections">{renderConnections()}</TabsContent>
              <TabsContent value="sync">{renderSyncPairs()}</TabsContent>
              <TabsContent value="history">{renderHistory()}</TabsContent>
              <TabsContent value="ftp">{renderFtp()}</TabsContent>
            </Tabs>
          )}
        </motion.div>

        {renderPairDialog()}
        {renderFtpDialog()}
        {renderFtpCredentials()}
      </main>
    </div>
  );
}

// ==================== SUB-COMPONENTS ====================

function SyncPairDialog({ connection, galleries, onClose, onBrowse, folderBrowser, onSubmit }) {
  const [galleryId, setGalleryId] = useState('');
  const [folderId, setFolderId] = useState('');
  const [folderPath, setFolderPath] = useState('');
  const [direction, setDirection] = useState('import');
  const [browsing, setBrowsing] = useState(false);

  const handleBrowse = async (id) => {
    setBrowsing(true);
    await onBrowse(id);
    setBrowsing(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nowa para synchronizacji</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Galeria</label>
            <Select value={galleryId} onValueChange={setGalleryId}>
              <SelectTrigger><SelectValue placeholder="Wybierz galerię" /></SelectTrigger>
              <SelectContent>
                {galleries.map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Folder w chmurze</label>
            <div className="flex gap-2">
              <Input
                value={folderPath}
                onChange={(e) => setFolderPath(e.target.value)}
                placeholder="Ścieżka folderu"
                className="flex-1"
              />
              <Button variant="outline" onClick={() => handleBrowse(null)} disabled={browsing}>
                {browsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderOpen className="w-4 h-4" />}
              </Button>
            </div>

            {/* Folder browser */}
            {folderBrowser && folderBrowser.connectionId === connection.id && (
              <div className="mt-2 border rounded-md max-h-60 overflow-y-auto">
                {folderBrowser.items.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">Pusty folder</p>
                ) : (
                  folderBrowser.items.filter(i => i.is_folder).map(item => (
                    <button
                      key={item.id}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center gap-2 ${
                        folderId === item.id ? 'bg-secondary' : ''
                      }`}
                      onClick={() => { setFolderId(item.id); setFolderPath(item.path || item.name); }}
                      onDoubleClick={() => handleBrowse(item.id)}
                    >
                      <FolderOpen className="w-4 h-4 text-yellow-500" />
                      {item.name}
                      <ChevronRight className="w-3 h-3 ml-auto text-muted-foreground" />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Kierunek</label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {DIRECTIONS.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anuluj</Button>
          <Button
            disabled={!galleryId || (!folderId && !folderPath)}
            onClick={() => onSubmit(connection.id, galleryId, folderId, folderPath, direction)}
          >
            Utwórz
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FtpCreateForm({ galleries, onSubmit, onClose }) {
  const [galleryId, setGalleryId] = useState('');
  const [displayName, setDisplayName] = useState('');

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium mb-1 block">Galeria docelowa</label>
        <Select value={galleryId} onValueChange={setGalleryId}>
          <SelectTrigger><SelectValue placeholder="Wybierz galerię" /></SelectTrigger>
          <SelectContent>
            {galleries.map(g => (
              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-medium mb-1 block">Nazwa (opcjonalna)</label>
        <Input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="np. Aparat Canon R5"
        />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Anuluj</Button>
        <Button disabled={!galleryId} onClick={() => onSubmit(galleryId, displayName)}>
          Utwórz konto
        </Button>
      </DialogFooter>
    </div>
  );
}

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
